import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getUserList(
    $filter: userFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getUserList(filter: $filter, pagination: $pagination, sort: $sort) {
      items {
        organization {
          id
          name
          shortName
          email
          mobile
          logoUrl
        }
        systemUser {
          name
          username
        }
        photo
        mobile
        email
        code
        name
        id
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.UserListState = {
  result: null,
  error: null,
  loading: false
};

export const getUserListRequest = createAsyncThunk<
  TYPES.UserList,
  {
    filter?: TYPES.UserFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'user/get_user_list',
  async (
  params: {
    filter?: TYPES.UserFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getUserList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getUserListSlice = createSlice({
  name: 'get_user_list',
  initialState: initialState,
  reducers: {
    getUserListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getUserListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getUserListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getUserListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getUserListClear } = getUserListSlice.actions;

export const getUserList = createSelector(
  (state: RootState) => state.user.getUserList.result,
  (state: RootState) => state.user.getUserList.error,
  (state: RootState) => state.user.getUserList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getUserListSlice.reducer;